import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import { usePageTitle } from 'contexts/PageTitleContext';
import { ReturnCancelRequestDetailsView } from './ReturnCancelRequestTable';
import { saveReturnCancelStatusTab } from './returnCancelRequestTabStorage';

const matchesRouteOrderId = (item, routeOrderId) => {
  if (!item || !routeOrderId) return false;
  return (
    String(item.id) === String(routeOrderId) ||
    String(item.order_id) === String(routeOrderId) ||
    String(item.order?.order_id) === String(routeOrderId) ||
    String(item.order?.id) === String(routeOrderId)
  );
};

function extractRequestRowsFromResponse(response) {
  if (!response || typeof response !== 'object') return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.results)) return response.results;
  if (Array.isArray(response.data)) return response.data;

  const dataObj = response.data;
  if (dataObj && typeof dataObj === 'object' && !Array.isArray(dataObj)) {
    if (Array.isArray(dataObj.results)) return dataObj.results;
    if (Array.isArray(dataObj.data)) return dataObj.data;
    const keys = [
      'orders',
      'cancel_requests',
      'return_requests',
      'requests',
      'Order',
      'CancelRequest',
      'ReturnRequest',
    ];
    for (const k of keys) {
      if (Array.isArray(dataObj[k])) return dataObj[k];
    }
    const firstArray = Object.values(dataObj).find((v) => Array.isArray(v));
    if (firstArray) return firstArray;
  }
  return [];
}

const ReturnCancelRequestDetails = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPaymentOrderId, setCheckingPaymentOrderId] = useState(null);

  useEffect(() => {
    setPageTitle('Return & Cancel Request');
  }, [setPageTitle]);

  const fetchOrder = useCallback(async () => {
    if (!orderId) {
      setOrder(null);
      setLoading(false);
      return;
    }

    const decodedOrderId = decodeURIComponent(orderId);
    const stateOrder = location.state?.order;
    const statusFilter = location.state?.statusFilter || 'Cancelled';

    if (stateOrder && matchesRouteOrderId(stateOrder, decodedOrderId)) {
      setOrder(stateOrder);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userId = localStorage.getItem('user_id');
      const endpoint =
        statusFilter === 'Cancelled'
          ? API_ENDPOINTS.Get_cancel_request
          : API_ENDPOINTS.Get_return_request;
      const url = `${endpoint}?user_id=${userId}&search=${encodeURIComponent(decodedOrderId)}&page_size=50`;
      const response = await apiService.get(url);
      const raw = extractRequestRowsFromResponse(response);
      const list = raw.map((item) => ({
        ...(item.order || item),
        id: item?.order?.id ?? item?.order_id ?? item?.id,
      }));
      const found = list.find((item) => matchesRouteOrderId(item, decodedOrderId)) || null;
      setOrder(found);
    } catch (error) {
      console.error('Error fetching return/cancel request details:', error);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderId, location.state]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleCheckPaymentStatus = async (orderData) => {
    const targetOrderId = orderData?.order_id ?? orderData?.id;
    if (!targetOrderId) return;
    const userId = localStorage.getItem('user_id') || '';
    setCheckingPaymentOrderId(targetOrderId);
    try {
      const url = `${API_ENDPOINTS.Refund_button_click}?user_id=${encodeURIComponent(userId)}&order_id=${encodeURIComponent(targetOrderId)}`;
      const response = await apiService.get(url);
      if (response?.success) {
        await fetchOrder();
      } else if (!response?.success) {
        alert(response?.message || 'Check payment status failed.');
      }
    } catch (error) {
      console.error('Check payment status error:', error);
      alert(error?.message || 'Check payment status failed.');
    } finally {
      setCheckingPaymentOrderId(null);
    }
  };

  const statusFilter = location.state?.statusFilter || 'Cancelled';

  const handleBack = () => {
    saveReturnCancelStatusTab(statusFilter);
    navigate('/dashboard/return-cancel-request', { state: { statusFilter } });
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
      >
        Back to Return & Cancel Requests
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : order ? (
        <Card sx={{ overflow: 'visible' }}>
          <ReturnCancelRequestDetailsView
            order={order}
            onCheckPaymentStatus={handleCheckPaymentStatus}
            checkingPaymentOrderId={checkingPaymentOrderId}
          />
        </Card>
      ) : (
        <Typography color="text.secondary">Order not found.</Typography>
      )}
    </Box>
  );
};

export default ReturnCancelRequestDetails;
